import React, { useState, useEffect } from 'react';
import { getProducts } from '../../lib/api';
import Analytics from '../../utils/analytics';

const TAGS = {
    'pkg_polished_professional': ['Video Ready', 'Upper Body'],
    'pkg_first_impression': ['Best Seller', 'Full Body'],
    'pkg_essential_sanctuary': ['Luxury', 'Complete Ritual'],
};

export default function ServiceSelection({ selectedPackage, selectedAddOns, onSelect }) {
    const [packages, setPackages] = useState([]);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState('');

    useEffect(() => {
        getProducts()
            .then(data => {
                setPackages(data);
                setLoading(false);

                // Analytics: Log the items the user is viewing
                const items = data.map(pkg => ({
                    item_name: pkg.name,
                    price: pkg.priceCents / 100
                }));
                Analytics.pushEcommerceEvent('view_item_list', { items });
            })
            .catch(err => {
                setError(err.message);
                setLoading(false);
            });
    }, []);

    const handleSelectPackage = (pkg) => {
        // If clicking same package, deselect
        if (selectedPackage?.id === pkg.id) {
            onSelect(null, []);
        } else {
            onSelect(pkg, []);
        }
    };

    const handleToggleAddon = (addon) => {
        const exists = selectedAddOns.find(a => a.name === addon.name);

        // Define standard analytics item payload
        const itemPayload = {
            item_name: addon.name,
            price: addon.priceCents / 100
        };

        if (exists) {
            onSelect(selectedPackage, selectedAddOns.filter(a => a.name !== addon.name));
            Analytics.pushEcommerceEvent('remove_from_cart', {
                currency: "CAD",
                value: itemPayload.price,
                items: [itemPayload]
            });
        } else {
            onSelect(selectedPackage, [...selectedAddOns, addon]);
            Analytics.pushEcommerceEvent('add_to_cart', {
                currency: "CAD",
                value: itemPayload.price,
                items: [itemPayload]
            });
        }
    };

    // Calculate totals
    const basePrice = selectedPackage ? selectedPackage.priceCents : 0;
    const addonPrice = selectedAddOns.reduce((sum, a) => sum + a.priceCents, 0);
    const totalPrice = basePrice + addonPrice;
    const baseDuration = selectedPackage ? selectedPackage.durationMinutes : 0;
    const addonDuration = selectedAddOns.reduce((sum, a) => sum + a.durationMinutes, 0);
    const totalDuration = baseDuration + addonDuration;

    if (loading) {
        return (
            <div className="wizard-step">
                <div className="loading-spinner">Loading packages...</div>
            </div>
        );
    }

    if (error) {
        return (
            <div className="wizard-step">
                <div className="error-banner">{error}</div>
            </div>
        );
    }

    return (
        <div className="wizard-step package-selection">
            <h2>Choose Your Experience</h2>
            <p className="step-subtitle">Exclusive Founding Member packages — crafted for you.</p>

            <div className="package-grid">
                {packages.map((pkg) => {
                    const isSelected = selectedPackage?.id === pkg.id;
                    const tags = TAGS[pkg.id] || [];

                    return (
                        <div
                            key={pkg.id}
                            className={`package-card ${isSelected ? 'selected' : ''}`}
                            onClick={() => handleSelectPackage(pkg)}
                        >
                            {/* Tags */}
                            <div className="package-tags">
                                {tags.map(tag => (
                                    <span key={tag} className={`tag ${tag === 'Best Seller' ? 'tag-highlight' : ''}`}>
                                        {tag}
                                    </span>
                                ))}
                            </div>

                            <h3 className="package-name">{pkg.name}</h3>

                            <div className="package-price">
                                <span className="price-amount">{pkg.price}</span>
                            </div>

                            <div className="package-duration">
                                <span>⏱ {pkg.durationMinutes} minutes</span>
                            </div>

                            <p className="package-description">{pkg.description}</p>

                            {/* Add-ons */}
                            {isSelected && pkg.addOns && pkg.addOns.length > 0 && (
                                <div className="package-addons" onClick={(e) => e.stopPropagation()}>
                                    <div className="addon-divider"></div>
                                    {pkg.addOns.map((addon, idx) => {
                                        const isAddonSelected = selectedAddOns.some(a => a.name === addon.name);
                                        return (
                                            <label key={idx} className={`addon-row ${isAddonSelected ? 'addon-selected' : ''}`}>
                                                <input
                                                    type="checkbox"
                                                    checked={isAddonSelected}
                                                    onChange={() => handleToggleAddon(addon)}
                                                />
                                                <span className="addon-label">
                                                    Add {addon.name} (+{addon.price})
                                                </span>
                                                <span className="addon-original-price">
                                                    <del>{addon.originalPrice}</del>
                                                </span>
                                            </label>
                                        );
                                    })}
                                </div>
                            )}

                            <div className="package-select-indicator">
                                {isSelected ? '✓ Selected' : 'Select'}
                            </div>
                        </div>
                    );
                })}
            </div>

            {/* Totals */}
            {selectedPackage && (
                <div className="selection-summary">
                    <div className="summary-item">
                        <span>Package:</span>
                        <span>{selectedPackage.name}</span>
                    </div>
                    {selectedAddOns.length > 0 && selectedAddOns.map(a => (
                        <div key={a.name} className="summary-item addon-summary">
                            <span>+ {a.name}:</span>
                            <span>+${(a.priceCents / 100).toFixed(2)}</span>
                        </div>
                    ))}
                    <div className="summary-total">
                        <span>Total: ${(totalPrice / 100).toFixed(2)}</span>
                        <span className="summary-duration">⏱ {totalDuration} min</span>
                    </div>
                </div>
            )}
        </div>
    );
}
